/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import {
    fork,
    takeLatest,
    call,
    select,
    put,
    take,
    getContext,
} from 'redux-saga/effects';
import QueryString from 'qs';
import { END } from 'redux-saga';
import mbpLogger from 'mbp-logger';
import mbpUtil from 'mbp-api-util';
import Cookies from 'universal-cookie';
import sdkFeatureFlags from 'feature-flags-sdk';
import configClient from 'mbp-config-server-api';

import * as helper from './App-Helper';
import {
    loadApp,
    setFeatureFlag,
    setBrandConfig,
} from './App-Actions';
import {
    getProfileInfo,
    getUserType,
    getUserRole,
} from '../../../Checkout/ducks/Global/Global-Selectors';
import {
    setProfileEmailId,
    loadedProfileUserRole,
    logUserLoginInSessionReady,
} from '../../../Checkout/ducks/Global/Global-Actions';

import memCache from '../../../../../app/helpers/memCache/memCache';
import { initSSR, loadAppShell, setAppShellLoadState } from '../../../App/App-Actions';
import { workerSagaCreateEnterpiseId, workerSagaUpdateEnterpriseId } from '../../../Member/ducks/EnterpriseId/EnterpriseId-Operations';
import isDesktopMobileTablet from '../../../../../app/helpers/DesktopMobile/isDesktopMobileTablet';
import { getBrandIdByHostName } from '../../../App/App-Helpers';
import { getCurrentUser } from '../../../App/App-Selectors';
// import { getBrand } from '../../../Common/Common-Selectors';
import { onLoadBrand } from '../../../App/ducks/Brand/Brand-Operations';
import { orderOperations } from '../../../Order';
import { configLoaded } from '../../../App/ducks/Config/Config-Actions';

const REACT_APP_INJECTABLE = mbpUtil.getEnv('REACT_APP_INJECTABLE');

function* getConfig({ config, domain }) {
    try {
        mbpLogger.logDebug({
            config,
            domain,
            function: 'getConfig',
            appName: process.env.npm_package_name,
            module: 'mbp-pwa-ui',
            message: 'Get Config | START',
        });

        const host = (typeof window !== 'undefined') ? mbpUtil.getEnv('APP_WC_HOST') : mbpUtil.getEnv('APP_CONFIG_HOST_SSR');
        const topic = config.topic;
        const branch = config.branch || 'master';

        const cacheKey = host + topic + branch;

        // lookup
        let configResponse = memCache.get(cacheKey);
        if (!configResponse) {
            configResponse = yield call(
                configClient.getConfig,
                {
                    host,
                },
                topic,
                branch,
            );

            if (configResponse.status === 200) {
                memCache.set(cacheKey, configResponse);
            }
        }

        mbpLogger.logDebug({
            host,
            configResponse,
            function: 'getConfig',
            appName: process.env.npm_package_name,
            module: 'mbp-checkout',
            message: 'Config Service Response',
        });

        if (configResponse && configResponse.data && configResponse.data.propertySources.length) {
            const [configPropertySource] = configResponse.data.propertySources;

            mbpLogger.logDebug({
                configPropertySource,
                function: 'getConfig',
                appName: process.env.npm_package_name,
                module: 'mbp-checkout',
                message: 'Config data found.',
            });

            return helper.parseConfig(configPropertySource.source, domain);
        }
    } catch (ex) {
        mbpLogger.logError({
            config,
            domain,
            function: 'getConfig',
            appName: process.env.npm_package_name,
            module: 'mbp-checkout',
            jsError: ex,
            message: 'Failed to get config data from service.',
        });
    }

    return {};
}

function* onLoadConfig({ branch, domain }) {
    let config = {};
    let topic = 'ref-brand';

    try {
        // Uat2 have different repo that listen the mastr brand
        const appGraphqlEnv = mbpUtil.getEnv('APP_VERSION_ENV');
        if (appGraphqlEnv === 'uat2') {
            topic = 'ref-brand-beta';
        }

        mbpLogger.logDebug({
            topic,
            branch,
            function: 'onLoadConfig',
            appName: process.env.npm_package_name,
            module: 'mbp-checkout',
            message: 'Load Config | START',
        });

        config = yield call(getConfig, {
            config: {
                topic,
                branch,
            },
            domain,
        });

        if (Object.keys(config).length > 0) {
            config = {
                ...config,
                id: config['product-brand-id'],
                code: config['brand-id'],
                domain: config['domain-name'],
                storeId: config['store-id'],
            };
        }

        yield put(setBrandConfig(config));
    } catch (ex) {
        // Uat2 have different repo that listen the mastr brand
        const appGraphqlEnv = mbpUtil.getEnv('APP_VERSION_ENV');
        if (appGraphqlEnv === 'uat2') {
            topic = 'ref-brand-beta';
        }

        mbpLogger.logError({
            topic,
            branch,
            function: 'onLoadConfig',
            appName: process.env.npm_package_name,
            module: 'mbp-checkout',
            jsError: ex,
            message: 'Failed to load config data.',
        });
    }

    return config;
}

function getSessionKey() {
    let sessionKey = '';

    if (typeof window !== 'undefined') {
        const cookies = new Cookies();

        const currentDate = new Date();
        const expDate = new Date(currentDate.setFullYear(currentDate.getFullYear() + 1));

        sessionKey = cookies.get('sessionKey');

        if (!sessionKey) {
            sessionKey = helper.getUniqueKey();
        }

        cookies.set('sessionKey', sessionKey, {
            path: '/',
            encode: String,
            expires: expDate,
        });
    }

    return sessionKey;
}

function* getFlags(brand) {
    let flags = {};
    const profile = yield select(getProfileInfo);
    const device = helper.discoverDeviceType();
    const browser = helper.getBrowser();
    const operatingSystem = helper.getOperatingSystem();
    const userType = yield select(getUserType);
    const userStatus = yield select(getUserRole);
    const email = (profile.email) ? profile.email : '';
    const sessionKey = yield call(getSessionKey);

    const payload = {
        userKey: sessionKey || 'anonymous',
        userType: userType || 'G',
        userStatus,
        email,
        firstName: profile.firstName,
        lastName: profile.lastName,
        browser,
        operatingSystem,
        brand,
        version: 'rel',
        device,
    };

    // Uat2 have different repo that listen the flags
    const appGraphqlEnv = mbpUtil.getEnv('APP_VERSION_ENV');
    let topicFlag = 'mbp-ui';
    if (appGraphqlEnv === 'uat2') {
        topicFlag = 'mbp-ui-beta';
    }
    const featureFlagsResponse = yield call(sdkFeatureFlags.getFeatureFlags, {}, topicFlag, payload);

    flags = featureFlagsResponse.data;

    return flags;
}

function* onLoadFlags({
    brand,
}) {
    const topic = 'app-feature-flags';
    let flags = {};

    try {
        mbpLogger.logDebug({
            brand,
            function: 'onLoadFlags',
            appName: process.env.npm_package_name,
            module: 'mbp-checkout',
            message: 'Load Flag | START',
        });
        if (brand) {
            flags = yield call(getFlags, brand);

            yield put(setFeatureFlag(flags));
            if (REACT_APP_INJECTABLE === 'false') {
                yield put(configLoaded(brand, topic, flags));
            }
        }
    } catch (ex) {
        mbpLogger.logError({
            topic,
            brand,
            function: 'onLoadFlags',
            appName: process.env.npm_package_name,
            module: 'mbp-checkout',
            jsError: ex,
            message: 'Failed to load flag data.',
        });
    }

    return flags;
}

function* workerLoadApp() {
    try {
        const host = (typeof window !== 'undefined') ? window.location.hostname : '';
        const brandDomain = helper.getBrandDomain(host);

        yield fork(onLoadConfig, { domain: brandDomain });

        yield fork(onLoadFlags, { brand: brandDomain });
    } catch (ex) {
        mbpLogger.logError({
            function: 'workerLoadApp',
            appName: process.env.npm_package_name,
            module: 'mbp-checkout',
            jsError: ex,
            message: 'load App - Checkout',
        });
    }
}

function* watchLoadApp() {
    yield takeLatest([
        loadApp().type,
    ], workerLoadApp);
}

function* onLoadAppShell({
    appShellLoaded,
    fastlyDeviceType,
    history,
    hostname,
    isBot,
    location,
    shopperManagerId,
    siteVariant,
    userAgent,
    userKey,
}) {
    // const mbpLogger = yield getContext('mbpLogger');
    const splitCartId = location?.pathname?.split?.('/');
    const cartId = splitCartId?.[splitCartId?.length - 1];
    yield call(orderOperations.setOrderId, {
        data: [cartId],
    });

    const cookies = yield getContext('cookies');
    try {
        mbpLogger.logDebug({
            hostname,
            path: location.pathname,
            search: location.search,
            shopperManagerId,
            isBot,
            userAgent,
            fastlyDeviceType,
            appShellLoaded,
            function: 'onLoadAppShell',
            appName: process.env.npm_package_name,
            module: 'mbp-pwa-ui',
            message: 'Load App | START',
        });
        if (location.search) {
            console.log({
                cookies,
                hostname,
                path: location.pathname,
                search: location.search,
                shopperManagerId,
                isBot,
                userAgent,
                fastlyDeviceType,
                appShellLoaded,
                function: 'onLoadAppShell',
                module: 'mbp-pwa-ui',
                message: 'Load App | START',
            });
        }
        const user = yield select(getCurrentUser);
        const brandId = getBrandIdByHostName(hostname);
        const brand = yield call(onLoadBrand, { user, currentBrandId: brandId, hostname });
        if (!appShellLoaded) {
            let deviceType = '';
            if (isBot) {
                deviceType = isDesktopMobileTablet(userAgent, hostname);
            } else {
                deviceType = fastlyDeviceType || isDesktopMobileTablet(userAgent);
            }

            yield put(initSSR({
                hostname,
                path: location.pathname,
                shopperManagerId,
                isBot,
                userAgent,
                deviceType,
                fastlyDeviceType,
                userKey,
                siteVariant,
            }));

            yield call(onLoadFlags, { brand: brand?.['domain'] });
            yield put(setAppShellLoadState(true));
        }

        // CSR Ops Only
        if (typeof window !== 'undefined') {
            const featureFlags = true;
            const params = QueryString.parse(location?.search?.replace('?', '')) || {};
            const localStorageValueOfEnterpriseId = window.localStorage.getItem('enterpriseId');
            // Enterprise Id
            if (featureFlags) {
                if (localStorageValueOfEnterpriseId && params.mi_u) {
                    yield call(workerSagaUpdateEnterpriseId, params);
                } else {
                    yield call(workerSagaCreateEnterpiseId, params);
                }
            }
        }
        mbpLogger.logDebug({
            brand,
            user,
            hostname,
            location,
            shopperManagerId,
            isBot,
            userAgent,
            fastlyDeviceType,
            function: 'onLoadAppShell',
            appName: process.env.npm_package_name,
            module: 'mbp-pwa-ui',
            message: 'onLoadAppShell | Complete',
        });
    } catch (ex) {
        mbpLogger.logError({
            hostname,
            location,
            shopperManagerId,
            isBot,
            userAgent,
            fastlyDeviceType,
            function: 'onLoadAppShell',
            appName: process.env.npm_package_name,
            module: 'mbp-pwa-ui',
            jsError: ex,
            message: 'Failed to load app data.',
        });
    }
}

function* watchLoadAppShell() {
    let action = yield take(loadAppShell().type);

    while (action !== END) {
        yield call(onLoadAppShell, action.payload);
        action = yield take(loadAppShell().type);
    }
}

function* workerGetFeatureFlags() {
    const host = (typeof window !== 'undefined') ? window.location.hostname : '';
    const brandDomain = helper.getBrandDomain(host);
    yield fork(onLoadFlags, { brand: brandDomain });
}

function* watchUserProfileChangeGetFeatureFlags() {
    yield takeLatest([
        setProfileEmailId().type,
        loadedProfileUserRole().type,
        logUserLoginInSessionReady().type,
    ], workerGetFeatureFlags);
}

let watchers = [
    fork(watchLoadApp),
    fork(watchUserProfileChangeGetFeatureFlags),
];

if (REACT_APP_INJECTABLE === 'false') {
    watchers = [
        fork(watchLoadAppShell),
        fork(watchLoadApp),
        fork(watchUserProfileChangeGetFeatureFlags),
    ];
}

export {
    watchers,
};

export default {};
